import { SessionHistoryRow } from "../lib/queries";

/** How many weeks of training the chart covers. */
const WEEKS = 8;
const MS_PER_WEEK = 7 * 24 * 60 * 60 * 1000;

interface Week {
  label: string;
  sessions: number;
  flagged: number;
}

/**
 * Sessions per week for the last {@link WEEKS} weeks.
 *
 * Deliberately plain divs rather than a charting library: this is one series of small integers, and a
 * chart dependency would be more bytes than the entire rest of the page for something CSS heights
 * already express. It also keeps the artifact self-contained.
 *
 * Weeks are counted back from now rather than from the first session, so an athlete who stopped training
 * three weeks ago shows three empty columns -- which is the thing the coach actually needs to notice.
 */
function bucketByWeek(sessions: SessionHistoryRow[], now: number): Week[] {
  const weeks: Week[] = Array.from({ length: WEEKS }, (_, i) => ({
    label: i === WEEKS - 1 ? "This week" : `${WEEKS - 1 - i}w ago`,
    sessions: 0,
    flagged: 0,
  }));

  for (const session of sessions) {
    const completed = new Date(session.completed_at).getTime();
    if (Number.isNaN(completed)) continue;
    const weeksAgo = Math.floor((now - completed) / MS_PER_WEEK);
    if (weeksAgo < 0 || weeksAgo >= WEEKS) continue;
    const bucket = weeks[WEEKS - 1 - weeksAgo];
    bucket.sessions += 1;
    if (session.issue_flagged) bucket.flagged += 1;
  }

  return weeks;
}

export function ProgressChart({ sessions, now = Date.now() }: { sessions: SessionHistoryRow[]; now?: number }) {
  const weeks = bucketByWeek(sessions, now);
  const peak = Math.max(1, ...weeks.map((w) => w.sessions));

  return (
    <section>
      <h2>Training volume</h2>
      {sessions.length === 0 ? (
        <p className="empty-state">Nothing logged yet.</p>
      ) : (
        <ul className="progress-chart" aria-label="Sessions per week for the last eight weeks">
          {weeks.map((week) => (
            <li key={week.label} className="progress-bar-col">
              {/* The count is the accessible value; the bar is decoration on top of it. */}
              <span
                className={week.flagged > 0 ? "progress-bar flagged" : "progress-bar"}
                style={{ height: `${(week.sessions / peak) * 100}%` }}
                aria-hidden="true"
              />
              <span className="progress-count">{week.sessions}</span>
              <span className="progress-label">{week.label}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
